/* eslint-disable no-param-reassign */
import {useCallback, useState} from 'react';

import {SendingFilesSchema} from '../validations';
import {useFacePhotoStore, useGuardianStore} from '../../../store';
import {useUploadDocMutation} from '../../../queries/useUploadDocMutation';
import {useUploadFileMutation} from '../../../queries/useUploadFileMutation';
import {usePreRegisterMutation} from '../../../queries/usePreRegisterMutation';
import {useRegisterDocumentMutation} from '../../../queries/useRegisterDocumentMutation';
import {useBiometricWizard} from '../../../hooks/useBiometricWizard';
import {useFacialBiometricsStore} from 'presentation/modules/booking/shared/store/useFacialBiometricsStore';
import {BiofaceResponseCode, SUCCESS_STATUS_LIST} from '../constants';
import {extractBase64} from '../../../utils';

import {getUserInfoFromToken} from 'presentation/utils';
import {logEvent} from 'main/analytics/analytics';
import {LogEventFacialBiometricsEnum} from 'main/analytics/logEventEnum';

export const useFileHandler = () => {
	const [file, setFile] = useState<File | null>(null);
	const [documentError, setDocumentError] = useState({
		isError: false,
		message: '',
	});
	const [isLoading, setIsLoading] = useState(false);
	const [isOnDrop, setIsOnDrop] = useState(false);
	const {frontFace, rightFace, leftFace} = useFacePhotoStore(state => state);
	const {next} = useBiometricWizard();
	const setHasFacialBiometrics = useFacialBiometricsStore(state => state.setHasFacialBiometrics);
	const setGuardians = useFacialBiometricsStore(state => state.setGuardians);
	const {isTea, guardians} = useFacialBiometricsStore(state => state);
	const {selectedGuardianId} = useGuardianStore();
	const {cdPessoa: tokenCdPessoa} = getUserInfoFromToken();
	const cdPessoa = isTea && selectedGuardianId ? String(selectedGuardianId) : tokenCdPessoa;
	const [errorCode, setErrorCode] = useState<number | null>(null);

	const {mutateAsync: uploadDoc} = useUploadDocMutation();
	const {mutateAsync: uploadFile} = useUploadFileMutation();
	const {mutateAsync: preRegister} = usePreRegisterMutation();
	const {mutateAsync: registerDocument} = useRegisterDocumentMutation();

	const validateFile = (file: File) => {
		const result = SendingFilesSchema.safeParse(file);

		if (!result.success) {
			setDocumentError({isError: true, message: result.error.issues[0].message});
			setIsLoading(false);
			return false;
		}
		return true;
	};

	const processFile = async (newFile: File) => {
		setIsLoading(true);
		const isValid = validateFile(newFile);

		if (!isValid) return;
		setDocumentError({isError: false, message: ''});

		setFile(newFile);
		setIsLoading(false);
	};

	const onDrop = useCallback((event: React.DragEvent) => {
		event.preventDefault();
		setIsOnDrop(false);

		const newFile = event.dataTransfer.files?.[0];
		if (newFile) processFile(newFile);
	}, []);

	const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const newFile = event.target.files?.[0];
		if (newFile) processFile(newFile);
	}, []);

	const onDragOver = (event: React.DragEvent) => {
		event.preventDefault();
		setIsOnDrop(true);
	};

	const onDragLeave = () => setIsOnDrop(false);

	const removeFile = () => {
		setFile(null);
		setDocumentError({isError: false, message: ''});
	};

	function converterParaBase64(selectedFile: File | null): Promise<string> {
		return new Promise((resolve, reject) => {
			if (!selectedFile) {
				reject(new Error('No file provided'));
				return;
			}
			const reader = new FileReader();

			reader.readAsDataURL(selectedFile);

			reader.onload = () => resolve(reader.result as string);
			reader.onerror = error => reject(error);
		});
	}

	const onSubmit = async () => {
		if (!file) {
			setDocumentError({isError: true, message: ''});
			return;
		}

		setIsLoading(true);
		setErrorCode(null);
		try {
			const preRegisterData = await preRegister({
				cdPessoa,
				imagens: [{imgb64: frontFace, index: 1}],
				app: 'PORTAL_BENEFICIARIO',
				infAdd: '',
				fAtualizar: 'S',
			});

			if (preRegisterData?.cod !== BiofaceResponseCode.PRECADASTRO_SUCESSO) {
				setErrorCode(preRegisterData?.cod);
				return;
			}

			const docFotob64 = await converterParaBase64(file);
			const documentBase64 = extractBase64(docFotob64) as string;
			const fileExtension = file.name.split('.').pop() ?? '';

			await registerDocument({
				flUrgencia: 'N',
				flPrecadastro: 'S',
				cdPessoa,
				extensaoArquivo: fileExtension,
				imagemBase64: documentBase64,
			});

			const docData = await uploadDoc({
				imagemb64: frontFace,
				docFotob64: documentBase64,
			});

			if (docData?.cod !== BiofaceResponseCode.VERIFICACAO_POSITIVA) {
				setErrorCode(docData?.cod);
				return;
			}

			const fileData = await uploadFile({
				cdPessoa,
				imagens: [frontFace, rightFace, leftFace],
				app: 'PORTAL_BENEFICIARIO',
				fAtualizar: 'S',
				infAdd: '',
				carregarImagens: 'N',
			});

			if (!SUCCESS_STATUS_LIST.includes(fileData?.cod)) {
				setErrorCode(fileData?.cod);
				return;
			}

			logEvent({eventName: LogEventFacialBiometricsEnum.REGISTER_FACIAL_BIOMETRICS_SUCCESS});
			setHasFacialBiometrics(true);

			if (isTea && selectedGuardianId) {
				setGuardians(
					guardians.map(guardian =>
						guardian.personId === selectedGuardianId
							? {...guardian, facialRegistered: true}
							: guardian,
					),
				);
			}

			next();
		} catch (err) {
			setErrorCode(BiofaceResponseCode.ERRO_GENERICO);
		} finally {
			setIsLoading(false);
		}
	};

	return {
		file,
		documentError,
		setDocumentError,
		errorCode,
		isLoading,
		isOnDrop,
		onDrop,
		onChange,
		onDragOver,
		onDragLeave,
		removeFile,
		onSubmit,
		setFile,
	};
};
